import type WhiteboardController from '../controllers/WhiteboardController';
import { isMenuItemDefinition } from '../stylus/definitions';
import DraggableBrush from '../stylus/DraggableBrush';
import StylusShape from '../stylus/StylusShape';
import { type ShapeTabItem } from './ObjectMenu/ShapesTab';
import type StylusSvgStamp from '../stylus/StylusSvgStamp';
import type StylusConnector from '../stylus/StylusConnector';
import { raw } from '@aha-app/mvc';
import SimpleSelection from 'fabric/SimpleSelection';
import ToolDelete from '../tools/ToolDelete';
import type { FrameMenuStylus } from '../utils/frameMenu';

type AnyStylus =
  | ShapeTabItem
  | StylusSvgStamp
  | StylusConnector
  | FrameMenuStylus;

class ShapeDragManager {
  whiteboardController: WhiteboardController;

  // Shape being dragged
  private shape?: fabric.Object;

  // We pin the menu temporarily, this is to restore the previous pinned state.
  private wasMenuPinned: boolean;

  constructor(whiteboardController: WhiteboardController) {
    this.whiteboardController = whiteboardController;
    this.registerKeyBindings();
  }

  abortCreation() {
    const { shape } = this;
    if (!shape) return;
    this.shape = undefined;

    if (shape instanceof SimpleSelection) {
      ToolDelete.deleteObjects(shape.getObjects(), this.whiteboardController);
    }
    ToolDelete.deleteObjects([shape], this.whiteboardController);
  }

  startDrag(e: MouseEvent, item: AnyStylus) {
    const { canvas } = this.whiteboardController;
    const stylus = isMenuItemDefinition(item)
      ? this.whiteboardController.getStylus(item.stylusDefinition)
      : raw(item);

    // Can't drag non-brushes
    if (stylus instanceof StylusShape && stylus.nonBrushShape()) {
      return;
    }

    canvas.discardActiveObject();
    stylus.activate();
    const brush = canvas.freeDrawingBrush;
    if (brush instanceof DraggableBrush && brush.isDraggable()) {
      const mousePointer = canvas.getUncachedPointer(e);
      brush.startDragging(mousePointer).then(shape => {
        if (shape) {
          // Force menu to be pinned while we drag it
          this.wasMenuPinned = shape.controller.state.objectMenuPinned;
          shape.controller.state.objectMenuPinned = true;
          shape.moveToCoords({
            left: mousePointer.x - shape.width / 2,
            top: mousePointer.y - shape.height / 2,
          });

          shape.controller.actionResetStylus();
          shape.canvas._setupCurrentTransform(e, shape, true);
          if ('isDraggingFromMenu' in shape) {
            shape.isDraggingFromMenu = true;
          }
          // Simulate a mouse down on the new shape and let fabric take over
          shape.canvas._onMouseDown(e);
        }
        this.shape = shape ?? undefined;
      });

      window.addEventListener('mouseup', this.onMouseUp);
    }
  }

  private readonly onMouseUp = (e: MouseEvent) => {
    window.removeEventListener('mouseup', this.onMouseUp);
    this.whiteboardController.state.objectMenuPinned = this.wasMenuPinned;
    const { shape } = this;
    if (!shape) return;
    if ('isDraggingFromMenu' in shape) {
      shape.isDraggingFromMenu = false;
    }
    if (isOverAbortRegion(e)) {
      // Mouse is still over the menu, so delete shape
      this.abortCreation();
    } else {
      shape.canvas._currentTransform = null;
    }
    this.shape = undefined;
  };

  private registerKeyBindings() {
    // Using hotkeys.js causes whiteboard hotkeys to be enbled in places they shouldn't be
    // So we'll just bind to the window instead
    window.addEventListener(
      'keydown',
      (e: KeyboardEvent) => {
        if (e.key === 'Escape' || e.key === 'Esc') {
          this.abortCreation();
        }
      },
      { capture: true }
    );
  }
}

/**
 * Test if the mouse event happened over an HTMLElement with the data-drag-abort attribute
 */
function isOverAbortRegion(e: MouseEvent): boolean {
  let el: HTMLElement | null = document.elementFromPoint(
    e.clientX,
    e.clientY
  ) as HTMLElement;
  while (el && el !== document.body) {
    if (el.dataset.dragAbort) {
      return true;
    }
    el = el.parentElement;
  }
  return false;
}

export default ShapeDragManager;
